import React, { useEffect, useState } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import RecipeCard from '../components/RecipeCard';
import Masonry from 'react-masonry-css';

import { fetchWithCSRF } from '../utils/csrfUtils';
import { getIngredientImageUrl } from '../utils/imageUtils';
import supabase from '../supabaseClient';

import { useAuth } from '../context/AuthContext';

// Add styles for Masonry grid
const masonryStyles = `
    .my-masonry-grid {
        display: flex;
        width: auto;
        padding: 0 4px;
    }
    .my-masonry-grid_column {
        padding-left: 4px;
        background-clip: padding-box;
    }
    .masonry-item {
        margin-bottom: 0px;
        transform: scale(0.96);
        transition: transform 0.2s ease;
    }
`;

function IngredientDetails() {
    const { ingredientId } = useParams();
    const { sessionToken } = useAuth();
    const navigate = useNavigate();
    const [name, setName] = useState('');
    const [description, setDescription] = useState('');
    const [unitOfMeasurement, setUnitOfMeasurement] = useState('');
    const [estimatedShelfLife, setEstimatedShelfLife] = useState('');
    const [foodGroups, setFoodGroups] = useState([]);
    const [calories, setCalories] = useState('');
    const [protein, setProtein] = useState('');
    const [carbohydrate, setCarbohydrate] = useState('');
    const [fat, setFat] = useState('');
    const [servingSize, setServingSize] = useState('');
    const [micronutrients, setMicronutrients] = useState([]);
    const [estimatedCostPerUnitCents, setEstimatedCostPerUnitCents] = useState('');
    const [isPerishable, setIsPerishable] = useState(false);
    const [isVegetarian, setIsVegetarian] = useState(false);
    const [isVegan, setIsVegan] = useState(false);
    const [recipes, setRecipes] = useState([]);
    const [relatedRecipes, setRelatedRecipes] = useState([]);

    const [isAdmin, setIsAdmin] = useState(false);

    useEffect(() => {


        const id = ingredientId;

        //check if user is admin
        fetchWithCSRF('/api/v1/users/check_user', {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${sessionToken}`
            }
        })
        .then(response => response.json())
        .then(data => {
            if (!data.isAdmin) {
                setIsAdmin(false);
            } else {
                setIsAdmin(true);
            }
        })

        // Fetch ingredient details
        fetchWithCSRF(`/api/v1/ingredient/${id}`, {
            method: 'GET',
            headers: {
                'Authorization': `Bearer ${sessionToken}`
            }
        })
            .then(response => response.json())
            .then(data => {
                if (data.length > 0) {
                    setName(data[0].name);
                    setDescription(data[0].description);
                    setUnitOfMeasurement(data[0].unit_of_measurement);
                    setEstimatedShelfLife(data[0].estimated_shelf_life);
                    setServingSize(data[0].serving_size);
                    // setFoodGroups(data.food_groups);
                    setCalories(data[0].calories_per_base_unit);
                    setProtein(data[0].protein);
                    setCarbohydrate(data[0].carbohydrate);
                    setFat(data[0].fat);
                    setMicronutrients(data[0].micronutrients);
                    setEstimatedCostPerUnitCents(data[0].estimated_cost_per_unit_cents);
                    setIsPerishable(data[0].is_perishable);
                    setIsVegetarian(data[0].is_vegetarian);
                    setIsVegan(data[0].is_vegan);

                    
                    
                }
            })
            .catch(error => console.error('Error fetching ingredient details:', error));

        //fetch food groups from m2m_ingredient_food_group

        // Fetch recipes that use this ingredient - now getting full recipe data in one request
        fetchWithCSRF(`/api/v1/recipe-ingredient/uses_ingredient/${ingredientId}`, {
            headers: {
                'Authorization': `Bearer ${sessionToken}`
            }
        })
        .then(response => response.json())
        .then(data => {
            // The response contains a recipes array inside the first object
            if (data && data.length > 0 && data[0]) {
                // The data structure might be different - let's log it to see exactly what we get
                const recipes = data[0].recipe_id ? [data[0]] : data[0].recipes;
                
                const formattedRecipes = recipes.map(recipe => ({
                    id: recipe.recipe_id,  // Changed from recipe.id to recipe.recipe_id
                    name: recipe.recipe_name,  // Changed from recipe.name to recipe.recipe_name
                    calories: recipe.calories || 0,
                    prep_time: recipe.prep_time || 0,
                    cook_time: recipe.cook_time || 0
                }));
                setRelatedRecipes(formattedRecipes);
            }
        })
        .catch(error => console.error('Error fetching related recipes:', error));
    


    }, [ingredientId, sessionToken]);

    const handleEditClick = () => {
        navigate(`/ingredients/${ingredientId}/edit`);
    };

    return (
        <div className="ingredient-details container-fluid px-4 py-3">
            <style>{masonryStyles}</style>
            <h1 className="mb-4 text-center">{name}</h1>
            
            {/* Ingredient Image */}
            <div className="text-center mb-4">
                <div className="mx-auto mt-5" style={{ maxWidth: '400px' }}>
                    <img 
                        src={getIngredientImageUrl(ingredientId)} 
                        alt={name} 
                        className="img-fluid"
                        style={{ 
                            maxHeight: '200px',
                            objectFit: 'contain',
                            width: '100%',
                            borderRadius: '8px'
                        }}
                    />
                </div>
            </div>

            {/* Description */}
            <div className="text-center mb-4">
                <p className="lead">{description}</p>
            </div>

            {/* Nutrition Facts */}
            <div className="d-flex flex-column align-items-center mb-4">
                <div className="d-flex align-items-center gap-4">
                    {calories && (
                        <div className="text-center">
                            <div className="h4 mb-0">{Math.round(calories)}kcal</div>
                            <div className="text-muted small">calories</div>
                        </div>
                    )}
                    <div className="text-center">
                        <div className="h4 mb-0">{Math.round(protein)}g</div>
                        <div className="text-muted small">protein</div>
                    </div>
                    <div className="text-center">
                        <div className="h4 mb-0">{Math.round(carbohydrate)}g</div>
                        <div className="text-muted small">carbs</div>
                    </div>
                    <div className="text-center">
                        <div className="h4 mb-0">{Math.round(fat)}g</div>
                        <div className="text-muted small">fat</div>
                    </div>
                </div>
                <div className="mt-1">
                    <small className="text-muted">per {servingSize} {unitOfMeasurement}</small>
                </div>
            </div>

            {/* Additional Details */}
            <div className="row justify-content-center mb-4">
                <div className="col-md-6">
                    {/* <div className="card"> */}
                        {/* <div className="card-body"> */}
                            <ul className="list-unstyled">
                                <li className="mb-2">
                                    <ul className="list-unstyled ms-3 d-flex gap-4 justify-content-center">
                                        <li>
                                            Vegetarian <img src={isVegetarian ? '/icons/checkmark.svg' : '/icons/xmark.svg'} alt={isVegetarian ? 'Yes' : 'No'} style={{width: '16px', height: '16px'}} />
                                        </li>
                                        <li>
                                            Vegan <img src={isVegan ? '/icons/checkmark.svg' : '/icons/xmark.svg'} alt={isVegan ? 'Yes' : 'No'} style={{width: '16px', height: '16px'}} />
                                        </li>
                                    </ul>
                                </li>
                            </ul>
                        {/* </div> */}
                    {/* </div> */}
                </div>
            </div>

            {/* Edit Button */}
            {isAdmin && (
                <div className="text-center mb-4">
                    <button 
                        onClick={handleEditClick}
                        className="btn btn-primary"
                        style={{ 
                            backgroundColor: '#52cd92',
                            borderColor: 'transparent',
                            borderRadius: '4px',
                            color: 'white'
                        }}
                    >
                        Edit
                    </button>
                </div>
            )}

            {/* Related Recipes */}
            {relatedRecipes.length > 0 && (
                <div className="related-recipes">
                    <h2 className="text-center mb-4">Recipes with this ingredient</h2>
                    <Masonry
                        breakpointCols={{
                            default: 4,
                            1100: 3,
                            700: 2,
                            500: 2
                        }}
                        className="my-masonry-grid"
                        columnClassName="my-masonry-grid_column"
                    >
                        {relatedRecipes.map((recipe, index) => (
                            
                            <div key={recipe.id || `recipe-${index}`} className="masonry-item">
                                <RecipeCard recipe={recipe} />
                            </div>
                        ))}
                    </Masonry>
                </div>
            )}
        </div>
    );
}

export default IngredientDetails;